<script setup lang="ts">
import { useQueryClient } from '@tanstack/vue-query'
import { useField, useForm } from 'vee-validate'
import { computed, watch } from 'vue'
import * as yup from 'yup'
import { editDiscountAction } from '@/api/functions/settings/discounts-action'
import { useMutationApi } from '@/composables/useApi'
import { useToast } from '@/composables/useToast'
import { getErrorMessage } from '@/utils/errorUtils'

const props = defineProps({
  modelValue: Boolean,
  discount: {
    type: Object,
  },
  currencies: {
    type: Array,
  },
  discountTypeOptions: {
    type: Array,
  },
})

const emit = defineEmits(['update:modelValue', 'confirm', 'cancel'])

const toast = useToast()
const queryClient = useQueryClient()

// const typeMap = {
//     "ADMIN": 2,
//     "REFERRAL": 4,
//     "SYSTEM": 6
// };

// Validation schema
const schema = yup.object({
  credit_type: yup.string().required('نوع اعتبار الزامی است'),
  type_id: yup.string().required('نوع تخفیف الزامی است'),
  title: yup.string().required('عنوان تخفیف الزامی است'),
  description: yup.string().nullable(),
  currency_id: yup.string().required('ارز موردنظر الزامی است'),
  rate: yup.number().required('مقدار تخفیف الزامی است').typeError('باید عدد باشد'),
  volume_limit: yup.number().required('سقف حجمی تخفیف الزامی است').typeError('باید عدد باشد'),
  expiry_date: yup.string().nullable(),
})

// Form setup
const { handleSubmit, errors, resetForm, setValues } = useForm({
  validationSchema: schema,
  initialValues: {
    credit_type: '',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    type_id: '',
    title: '',
    description: '',
    currency_id: '',
    rate: null,
    volume_limit: null,
    expiry_date: null,
  },
})

// Fields
const { value: credit_type } = useField('credit_type')
const { value: type_id } = useField('type_id')
const { value: title } = useField('title')
const { value: description } = useField('description')
const { value: currency_id } = useField('currency_id')
const { value: rate } = useField('rate')
const { value: volume_limit } = useField('volume_limit')
const { value: expiry_date } = useField('expiry_date')

const showDatePicker = computed(() => credit_type.value === 'DURATION_BASED')

const setRequestValues = (discount: any) => {
  const currency = discount.currency

  setValues({
    credit_type: discount.credit_type || '',
    type_id: discount.type?.id || '',
    title: discount.title || '',
    description: discount.description || '',
    currency_id: Array.isArray(currency) ? (currency.length > 1 ? 'all' : currency[0]?.id || '') : currency?.id || '',
    rate: discount.rate || null,
    volume_limit: discount.volume_limit || null,
    expiry_date: discount.expiry_date || null,
  })
}

watch(
  () => props.discount,
  (discount) => {
    if (discount) {
      setRequestValues(discount)
    }
  },
  { immediate: true }
)

watch(
  () => props.modelValue,
  (isOpen) => {
    if (isOpen && props.discount) {
      setRequestValues(props.discount)
    }
  }
)

const currencyOptions = computed(() => [
  {
    label: 'همه ارزها',
    value: 'all',
  },
  ...(props.currencies ?? []),
])

// Mutation
const editDiscountMutation = useMutationApi({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mutationFn: ({ id, payload }: { id: string | number; payload: any }) => editDiscountAction(id, payload),
  onSuccess: () => {
    toast.success('تخفیف با موفقیت ویرایش شد', 'موفقیت')
    emit('confirm')
    closeModal()
    queryClient.invalidateQueries({ queryKey: ['discountDetails'] })
  },
  onError: (error: Error) => {
    toast.error(getErrorMessage(error), 'خطا')
  },
})

// Submit
const onSubmit = handleSubmit((values) => {
  if (!props.discount?.id) {
    toast.error('تخفیفی برای ویرایش انتخاب نشده است', 'خطا')
    return
  }

  editDiscountMutation.mutate({
    id: props.discount.id,
    payload: values,
  })
})

const closeModal = () => {
  emit('update:modelValue', false)
  emit('cancel')
  resetForm()
}
</script>

<template>
    <Modal :model-value="props.modelValue" @update:model-value="$emit('update:modelValue', $event)" width="600px"
        background="#FFF" :closable="true" title="ویرایش تخفیف" closeDirection="left" class="update-request">
        <form @submit.prevent="onSubmit" class="flex flex-col">

            <div class="flex-1">
                <div class="grid px-4 pt-6 pb-9 gap-2">
                    <Select v-model="credit_type" label="نوع اعتبار" variant="checkbox" :options="[
                        { label: 'یک‌بار مصرف', value: 'ONE_TIME_USE' },
                        { label: 'مدت‌دار', value: 'DURATION_BASED' }
                    ]" :error="errors.credit_type"
                        :disabled="editDiscountMutation.isPending.value || discount?.type?.title === 'SYSTEM'" />

                    <Select v-model="type_id" label="نوع تخفیف" variant="checkbox" :options="discountTypeOptions"
                        :error="errors.type_id"
                        :disabled="editDiscountMutation.isPending.value || discount?.type?.title === 'SYSTEM'" />

                    <Input v-model="title" name="title" label="عنوان تخفیف" type="text" :error="errors.title"
                        :disabled="editDiscountMutation.isPending.value || discount?.type?.title === 'SYSTEM'" />
                    <Input v-model="description" name="description" label="توضیحات تخفیف" type="text"
                        :error="errors.description"
                        :disabled="editDiscountMutation.isPending.value || discount?.type?.title === 'SYSTEM'" />

                    <Select v-model="currency_id" label="ارز موردنظر" variant="checkbox" :options="currencyOptions"
                        :error="errors.currency_id"
                        :disabled="editDiscountMutation.isPending.value || discount?.type?.title === 'SYSTEM'" />

                    <Input v-model="rate" name="rate" label="مقدار تخفیف(%)" type="number" :error="errors.rate"
                        :disabled="editDiscountMutation.isPending.value" />
                    <Input v-model="volume_limit" name="volume_limit" label="سقف حجمی تخفیف" type="number"
                        :error="errors.volume_limit" :disabled="editDiscountMutation.isPending.value" />

                    <AlirezaDatePicker v-if="showDatePicker" v-model="expiry_date" placeholder="تاریخ اعتبار" class=""
                        :error="errors.expiry_date" :disabled="editDiscountMutation.isPending.value" />
                </div>
            </div>

            <div class="sticky bottom-0 bg-white z-50 flex justify-between gap-6 w-full pt-3 pb-9 px-2 border-gray-200">
                <Button type="submit" label="ویرایش تخفیف" :isAdmin="true" class="w-9/12 h-12 rounded-xl"
                    :disabled="editDiscountMutation.isPending.value || !props.discount?.id"
                    :loading="editDiscountMutation.isPending.value" />
                <Button type="button" @click="closeModal" variant="outline" label="بازگشت" :isAdmin="true"
                    class="w-4/12 h-12 rounded-xl border-2" :disabled="editDiscountMutation.isPending.value" />
            </div>
        </form>
    </Modal>
</template>
